import { useTranslation } from 'react-i18next';

import { useCertify } from '@/features/certifiers/hooks/use-certifications';
import { openCredential, usePendingCredentials, useReviewCredential } from '@/features/credentials';
import { useMyProfile, useProviders } from '@/features/profile';
import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';

import { useSetCertifier, useSetVerified } from '../hooks/use-admin';

/**
 * Admin verifications: two adjacent sections — pending credential documents
 * (approve/reject) and the provider list with inline verify + certifier
 * moderation (per D-013 "moderation actions accessible inline"). Split out of
 * the old AdminRoute so the dashboard has a home for these separate concerns.
 */
export function AdminVerificationsRoute(): JSX.Element {
  const { t } = useTranslation();
  const providers = useProviders();
  const me = useMyProfile();
  const setVerified = useSetVerified();
  const setCertifier = useSetCertifier();
  const certify = useCertify();
  const pendingDocs = usePendingCredentials();
  const reviewDoc = useReviewCredential();

  const iAmCertifier = me.data?.isCertifier ?? false;

  return (
    <main className="page stack">
      <h1>{t('admin.verificationsTitle')}</h1>

      <h2>{t('credentials.reviewTitle')}</h2>
      <Async
        query={pendingDocs}
        isEmpty={(l) => l.length === 0}
        emptyMessage={t('credentials.reviewEmpty')}
      >
        {(docs) => (
          <div className="stack">
            {docs.map((d) => (
              <div key={d.id} className="card stack">
                <div className="row row--between">
                  <button
                    type="button"
                    className="btn btn--text"
                    onClick={() => openCredential(d.storagePath)}
                  >
                    {d.filename}
                  </button>
                  <span className="muted">{d.ownerName ?? ''}</span>
                </div>
                <div className="wrap">
                  <Button
                    loading={reviewDoc.isPending}
                    onClick={() => reviewDoc.mutate({ id: d.id, status: 'approved' })}
                  >
                    {t('credentials.approve')}
                  </Button>
                  <Button
                    variant="outline"
                    loading={reviewDoc.isPending}
                    onClick={() => reviewDoc.mutate({ id: d.id, status: 'rejected' })}
                  >
                    {t('credentials.reject')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Async>

      <h2>{t('admin.providers')}</h2>
      <Async query={providers} isEmpty={(l) => l.length === 0} emptyMessage={t('admin.empty')}>
        {(list) => (
          <div className="stack">
            {list.map((provider) => (
              <div key={provider.id} className="card stack">
                <div className="row row--between">
                  <div>
                    <strong>{provider.fullName}</strong>
                    {provider.city ? <p className="muted">{provider.city}</p> : null}
                  </div>
                  <div className="wrap">
                    <Badge
                      label={provider.verified ? t('profile.verified') : t('profile.unverified')}
                      color={
                        provider.verified ? 'var(--color-success)' : 'var(--color-on-surface-muted)'
                      }
                    />
                    {provider.isCertifier ? (
                      <Badge label={t('admin.certifierBadge')} color="var(--color-primary)" />
                    ) : null}
                  </div>
                </div>
                <div className="wrap">
                  <Button
                    variant="outline"
                    loading={setVerified.isPending}
                    onClick={() =>
                      setVerified.mutate({ profileId: provider.id, verified: !provider.verified })
                    }
                  >
                    {provider.verified ? t('admin.unverify') : t('admin.verify')}
                  </Button>
                  <Button
                    variant="outline"
                    loading={setCertifier.isPending}
                    onClick={() =>
                      setCertifier.mutate({
                        profileId: provider.id,
                        isCertifier: !provider.isCertifier,
                      })
                    }
                  >
                    {provider.isCertifier ? t('admin.removeCertifier') : t('admin.makeCertifier')}
                  </Button>
                  {iAmCertifier && provider.id !== me.data?.id ? (
                    <Button
                      variant="text"
                      loading={certify.isPending}
                      onClick={() => certify.mutate({ freelancerId: provider.id, note: null })}
                    >
                      {t('certifications.vouch')}
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}
